import React, { useEffect, useRef } from 'react';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  timestamp: number;
}

interface NotificationToastProps {
  notifications: Notification[];
  onRemove: (id: string) => void;
}

const NotificationToast: React.FC<NotificationToastProps> = ({ notifications, onRemove }) => {
  const timerSetRef = useRef(new Set<string>());

  useEffect(() => {
    // Set timer only for notifications that don't have one yet
    notifications.forEach(notification => {
      if (!timerSetRef.current.has(notification.id)) {
        timerSetRef.current.add(notification.id);
        setTimeout(() => {
          onRemove(notification.id);
          timerSetRef.current.delete(notification.id);
        }, 5000);
      }
    });
  }, [notifications, onRemove]);

  const getIcon = (type: string) => {
    // Return empty string to remove all emojis
    return '';
  };

  const getTypeStyles = (type: string) => {
    // All notifications use light grey theme to match the site
    return 'bg-gradient-to-r from-zinc-100 to-gray-100 border-zinc-300 text-zinc-800';
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes slide-in-right {
            from {
              transform: translateX(calc(100% + 20px)) scale(0.9);
              opacity: 0;
            }
            to {
              transform: translateX(0) scale(1);
              opacity: 1;
            }
          }
          
          @keyframes fade-out {
            from {
              transform: translateX(0) scale(1);
              opacity: 1;
            }
            to {
              transform: translateX(calc(100% + 20px)) scale(0.9);
              opacity: 0;
            }
          }
          
          .notification-slide-in {
            animation: slide-in-right 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
          }
          
          .notification-fade-out {
            animation: fade-out 0.3s ease-in forwards;
          }
        `
      }} />
      
      <div className="fixed top-4 right-4 z-50 flex flex-col-reverse gap-3 max-w-sm">
        {notifications.map((notification, index) => (
          <div
            key={notification.id}
            className={`
              transform transition-all duration-300 ease-in-out
              ${getTypeStyles(notification.type)}
              border rounded-xl p-4 backdrop-blur-sm
              notification-slide-in
              ${index > 0 ? 'translate-y-1 scale-95 opacity-90' : ''}
            `}
            style={{
              marginBottom: index * 6, // Stack effect
              zIndex: 1000 - index, // Ensure proper stacking order
            }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm leading-5 mb-1">{notification.title}</p>
                {notification.message && (
                  <p className="text-xs opacity-75 leading-4 whitespace-pre-wrap break-words">
                    {notification.message}
                  </p>
                )}
              </div>
              <button
                onClick={() => onRemove(notification.id)}
                className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full hover:bg-black hover:bg-opacity-10 transition-colors duration-200 text-current opacity-60 hover:opacity-100"
                aria-label="Close notification"
              >
                <span className="text-sm leading-none">×</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default NotificationToast;